import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import Text from '../Text';
import { Entypo } from '../Icons';
import SearchableSelect, { SelectOption } from '../SearchableSelect';
import { useStyles } from '~/hooks';
import i18n from '~/i18n';
import { FONT_SIZE_M } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    value?: string | null;
    options: SelectOption[];
    onChange: (value: string) => void;
    // i18n key shown while nothing is selected.
    placeholder: string;
    // i18n key for the picker sheet title; falls back to the placeholder.
    title?: string;
    // Search box is worth its space on long lists only.
    searchable?: boolean;
    hasError?: boolean;
    disabled?: boolean;
};

/**
 * Dropdown trigger that matches the app's text inputs and opens the shared SearchableSelect sheet.
 *
 * Replaces @react-native-picker/picker on forms: on web that renders a bare browser <select>, which
 * ignores the theme and sits at a different height than every field around it, and on iOS it renders
 * an always-open three-row wheel. This gives one control with one appearance on all three platforms.
 */
export default function Select({
    value,
    options,
    onChange,
    placeholder,
    title,
    searchable = true,
    hasError,
    disabled,
}: Props) {
    const styles = useStyles(stylesGenerator);
    const [open, setOpen] = useState(false);

    const selected = options.find((o) => o.value === value && o.value !== '');

    return (
        <>
            <Pressable
                disabled={disabled}
                onPress={() => setOpen(true)}
                style={({ pressed }) => [
                    styles.trigger,
                    hasError ? styles.triggerError : null,
                    disabled ? styles.triggerDisabled : null,
                    pressed ? styles.triggerPressed : null,
                ]}
                accessibilityRole="button"
                accessibilityLabel={i18n.t(title ?? placeholder)}
            >
                <View style={styles.labelWrap}>
                    <Text noTranslate numberOfLines={1} style={[styles.value, selected ? null : styles.placeholder]}>
                        {selected ? selected.label : i18n.t(placeholder)}
                    </Text>
                </View>
                <Entypo name="chevron-down" size={scaleSize(16)} style={styles.chevron} />
            </Pressable>
            <SearchableSelect
                visible={open}
                onClose={() => setOpen(false)}
                title={title ?? placeholder}
                options={options}
                selectedValue={value}
                onSelect={onChange}
                searchable={searchable}
            />
        </>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        trigger: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: scaleSize(4),
            borderColor: theme.equityValue,
            borderWidth: 1,
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(12),
            height: scaleSize(44),
        },
        triggerError: {
            borderColor: theme.alert,
        },
        triggerDisabled: {
            opacity: 0.5,
        },
        triggerPressed: {
            opacity: 0.6,
        },
        labelWrap: {
            flex: 1,
            marginRight: scaleSize(8),
        },
        value: {
            fontSize: FONT_SIZE_M,
            color: theme.desktopHeaderTextColor,
        },
        placeholder: {
            color: theme.textSecondary,
        },
        chevron: {
            color: theme.textSecondary,
        },
    });
